/*
 * Influence workspace data model: one shape for both data sources
 * (`/api/intelligence/network` and the older `/api/taxonomies/influence-analytics`),
 * plus filtering. Pure functions only.
 */
import type { InfluenceAnalyticsData, InfluenceCategory } from '../../../services/influencesService';
import type { EntityType, InfluenceAreaScore, NetworkGraph, NetworkLink, NodeRole } from '../types';

export interface InfluenceArea {
  id: string;
  key: string;
  label: string;
  color: string | null;
  description?: string | null;
}

export interface InfluenceEntity {
  id: string;
  type: EntityType;
  label: string;
  status: string;
  imageId?: string | null;
  category?: string | null;
  orgType?: string | null;
  location?: string | null;
  roles: NodeRole[];
  /** "Minister · Ministry of Mines" for people, type · location for organizations */
  subtitle: string;
  /** Highest score first */
  scores: InfluenceAreaScore[];
  scoreByArea: Record<string, InfluenceAreaScore>;
  /** 0 when unassessed */
  average: number;
  max: number;
  dominant: InfluenceAreaScore | null;
  assessed: boolean;
  /** Newest `updatedAt` among its scores */
  lastUpdated: string | null;
}

export interface InfluenceModel {
  areas: InfluenceArea[];
  entities: InfluenceEntity[];
  byId: Map<string, InfluenceEntity>;
  links: NetworkLink[];
  /** Loaded from the older analytics endpoint: no roles history, dates or confidence metadata */
  limited: boolean;
  generatedAt: string;
}

export const UNASSESSED_REGION = '__unassessed';

export const humanize = (value?: string | null) =>
  (value || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

function subtitleOf(type: EntityType, roles: NodeRole[], orgType?: string | null, location?: string | null, category?: string | null) {
  if (type === 'stakeholder') {
    const current = roles.filter((r) => r.isCurrent);
    const main = current[0] || roles[0];
    if (main) {
      const more = current.length > 1 ? ` +${current.length - 1}` : '';
      return [main.title, main.organizationName].filter(Boolean).join(' · ') + more;
    }
    return humanize(category) || location || 'Stakeholder';
  }
  return [humanize(orgType), location].filter(Boolean).join(' · ') || 'Organization';
}

const round = (n: number) => Math.round(n);

export function buildEntity(input: {
  id: string;
  type: EntityType;
  label: string;
  status?: string | null;
  imageId?: string | null;
  category?: string | null;
  orgType?: string | null;
  location?: string | null;
  roles?: NodeRole[];
  scores: InfluenceAreaScore[];
}): InfluenceEntity {
  const scores = input.scores
    .filter((s) => Number.isFinite(s.score))
    .slice()
    .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  const scoreByArea: Record<string, InfluenceAreaScore> = {};
  scores.forEach((s) => (scoreByArea[s.categoryId] = s));
  const total = scores.reduce((acc, s) => acc + s.score, 0);
  let lastUpdated: string | null = null;
  scores.forEach((s) => {
    if (s.updatedAt && (!lastUpdated || s.updatedAt > lastUpdated)) lastUpdated = s.updatedAt;
  });
  const roles = input.roles || [];
  return {
    id: input.id,
    type: input.type,
    label: input.label || 'Unnamed',
    status: input.status || 'approved',
    imageId: input.imageId ?? null,
    category: input.category ?? null,
    orgType: input.orgType ?? null,
    location: input.location ?? null,
    roles,
    subtitle: subtitleOf(input.type, roles, input.orgType, input.location, input.category),
    scores,
    scoreByArea,
    average: scores.length ? round(total / scores.length) : 0,
    max: scores.length ? scores[0].score : 0,
    dominant: scores[0] || null,
    assessed: scores.length > 0,
    lastUpdated,
  };
}

function areasFrom(categories: InfluenceCategory[], extra: InfluenceAreaScore[]): InfluenceArea[] {
  const areas: InfluenceArea[] = categories.map((c) => ({
    id: c.id,
    key: c.key,
    label: c.label,
    color: c.color || null,
    description: c.description || null,
  }));
  const known = new Set(areas.map((a) => a.id));
  extra.forEach((s) => {
    if (known.has(s.categoryId)) return;
    known.add(s.categoryId);
    areas.push({ id: s.categoryId, key: s.key, label: s.label, color: s.color || null });
  });
  return areas;
}

function finalize(areas: InfluenceArea[], entities: InfluenceEntity[], links: NetworkLink[], limited: boolean, generatedAt: string): InfluenceModel {
  const byId = new Map<string, InfluenceEntity>();
  entities.forEach((e) => byId.set(e.id, e));
  return { areas, entities, byId, links: links.filter((l) => byId.has(l.source) && byId.has(l.target)), limited, generatedAt };
}

/** From the intelligence network graph; area colours come from the taxonomy (source of truth) */
export function modelFromNetwork(graph: NetworkGraph, categories: InfluenceCategory[]): InfluenceModel {
  const allScores: InfluenceAreaScore[] = [];
  graph.nodes.forEach((n) => n.influence?.scores?.forEach((s) => allScores.push(s)));
  const areas = areasFrom(categories, allScores);
  const areaById = new Map<string, InfluenceArea>(areas.map((a) => [a.id, a] as [string, InfluenceArea]));
  const entities = graph.nodes.map((n) =>
    buildEntity({
      ...n,
      scores: (n.influence?.scores || []).map((s) => {
        const area = areaById.get(s.categoryId);
        return { ...s, label: area?.label || s.label, key: area?.key || s.key, color: area?.color || s.color || null };
      }),
    }),
  );
  return finalize(areas, entities, graph.links, false, graph.generatedAt || new Date().toISOString());
}

/** From the older analytics endpoint (server without /api/intelligence) */
export function modelFromAnalytics(data: InfluenceAnalyticsData): InfluenceModel {
  const typeOf = new Map<string, EntityType>(data.entities.map((e) => [e.id, e.type] as [string, EntityType]));
  const labelOf = new Map<string, string>(data.entities.map((e) => [e.id, e.label] as [string, string]));
  const roles = new Map<string, NodeRole[]>();
  const links: NetworkLink[] = [];

  data.connections.forEach((c, i) => {
    if (c.type === 'influence_correlation') return; // derived from scores, not a real tie
    const sType = typeOf.get(c.source);
    const tType = typeOf.get(c.target);
    if (!sType || !tType) return;
    if (c.type === 'assignment') {
      const person = sType === 'stakeholder' ? c.source : c.target;
      const org = sType === 'stakeholder' ? c.target : c.source;
      const list = roles.get(person) || [];
      list.push({ organizationId: org, organizationName: labelOf.get(org) || '', title: c.label || 'Member', isCurrent: c.isCurrent !== false });
      roles.set(person, list);
    }
    const kind: NetworkLink['kind'] = c.type === 'assignment' ? 'employment' : c.type === 'hierarchy' ? 'hierarchy' : 'relationship';
    const type: NetworkLink['type'] = sType === tType ? (sType === 'stakeholder' ? 'ss' : 'oo') : 'os';
    links.push({
      id: `${c.type}:${i}`,
      source: c.source,
      target: c.target,
      kind,
      type,
      label: c.label || null,
      category: c.category || null,
      weight: c.weight || (kind === 'hierarchy' ? 4 : kind === 'employment' ? (c.isCurrent === false ? 1 : 3) : 2),
      isCurrent: c.isCurrent !== false,
    });
  });

  const entities = data.entities.map((e) =>
    buildEntity({
      id: e.id,
      type: e.type,
      label: e.label,
      status: e.status,
      imageId: e.profileImageId || e.logoId || null,
      category: e.category,
      orgType: e.orgType,
      location: e.location,
      roles: roles.get(e.id),
      scores: e.scores.map((s) => ({
        categoryId: s.categoryId,
        key: s.categoryKey,
        label: s.categoryLabel,
        color: s.categoryColor || null,
        score: s.score,
        confidence: s.confidence ?? null,
        source: s.source || null,
        updatedAt: null,
      })),
    }),
  );
  const allScores: InfluenceAreaScore[] = [];
  entities.forEach((e) => e.scores.forEach((s) => allScores.push(s)));
  return finalize(areasFrom(data.categories, allScores), entities, links, true, new Date().toISOString());
}

/* ---------------- Filters ---------------- */

export interface InfluenceFilters {
  type: 'all' | EntityType;
  /** Enabled area ids; null = all areas */
  areaIds: string[] | null;
  /** Hide area scores below this value */
  minScore: number;
  /** Show entities without any score (gaps) */
  showUnassessed: boolean;
}

export const DEFAULT_FILTERS: InfluenceFilters = { type: 'all', areaIds: null, minScore: 0, showUnassessed: true };

export function activeFilterCount(filters: InfluenceFilters): number {
  return (
    (filters.type !== 'all' ? 1 : 0) +
    (filters.areaIds ? 1 : 0) +
    (filters.minScore > 0 ? 1 : 0) +
    (filters.showUnassessed ? 0 : 1)
  );
}

export interface FilteredView {
  areas: InfluenceArea[];
  areaIds: Set<string>;
  /** Assessed entities with at least one visible score, then unassessed ones when shown */
  entities: InfluenceEntity[];
  assessed: InfluenceEntity[];
  unassessed: InfluenceEntity[];
}

/** Scores of an entity that pass the area and min-score filters */
export function visibleScores(entity: InfluenceEntity, areaIds: Set<string>, minScore: number): InfluenceAreaScore[] {
  return entity.scores.filter((s) => areaIds.has(s.categoryId) && s.score >= minScore);
}

export function applyFilters(model: InfluenceModel, filters: InfluenceFilters): FilteredView {
  const areas = filters.areaIds ? model.areas.filter((a) => filters.areaIds!.includes(a.id)) : model.areas;
  const areaIds = new Set(areas.map((a) => a.id));
  const assessed: InfluenceEntity[] = [];
  const unassessed: InfluenceEntity[] = [];
  model.entities.forEach((e) => {
    if (filters.type !== 'all' && e.type !== filters.type) return;
    if (e.assessed) {
      if (visibleScores(e, areaIds, filters.minScore).length) assessed.push(e);
    } else if (filters.showUnassessed) {
      unassessed.push(e);
    }
  });
  return { areas, areaIds, assessed, unassessed, entities: assessed.concat(unassessed) };
}

const fold = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** Accent-insensitive match on name and role line */
export function matchesQuery(entity: InfluenceEntity, query: string): boolean {
  const q = fold(query.trim());
  if (!q) return true;
  return fold(entity.label).includes(q) || fold(entity.subtitle).includes(q);
}

export function searchEntities(entities: InfluenceEntity[], query: string, limit = 8): InfluenceEntity[] {
  const q = fold(query.trim());
  if (!q) return [];
  return entities
    .filter((e) => matchesQuery(e, q))
    .sort((a, b) => {
      const aStarts = fold(a.label).startsWith(q) ? 0 : 1;
      const bStarts = fold(b.label).startsWith(q) ? 0 : 1;
      return aStarts - bStarts || b.average - a.average || a.label.localeCompare(b.label);
    })
    .slice(0, limit);
}

export const profilePath = (entity: { id: string; type: EntityType }) =>
  `/${entity.type === 'organization' ? 'organizations' : 'stakeholders'}/${entity.id}`;
